/**
 * 벽돌 그리드 빌드 계획 (Phaser-free 순수 함수) — BattlefieldRunner에서 추출.
 *
 * 챕터/멜로디/던전 벽돌 배치의 "셀 생성 + 마킹" 단계를 담당한다.
 * Phaser GameObject(spawn/mask/physics)는 BattlefieldRunner에 남아 있다.
 */
import { BigNumber } from "@yjsgames/bignumber";
import type { SafeAreaInsets } from "@yjsgames/safe-area";
import {
  GRID,
  MYSTERY_BRICK,
  SHIELD_BRICK,
  ARMOR_BRICK,
  REGEN_BRICK,
  SPECIAL_RULES,
  specialBrickSpawnFields,
  MELODY,
} from "../data/balance.js";
import { ChapterController } from "../systems/ChapterController.js";
import { DungeonController } from "../systems/DungeonController.js";
import { worldForChapter, type WorldBrickTextures } from "../data/worldData.js";
import {
  CELL_MULTIPLIER_CAP,
  MIN_CHAPTER_BLOCKS,
  generateSeededBrickCells,
  layoutBrickCells,
} from "../systems/ChapterBrickLayout.js";
import { buildMelodyCells, resolveChapterLayout } from "../systems/MelodyChapterLayout.js";
import { markMysteryBricks } from "../systems/markMysteryBricks.js";
import {
  markSpecialBricks,
  resolveSpecialBrickWeights,
} from "../systems/markSpecialBricks.js";
import {
  specialBrickHpFactor,
  specialBrickGoldMult,
} from "../objects/brickAppearance.js";
import { brickStrength01 } from "../objects/brickStrength.js";
import { makeRng } from "../util/seededRng.js";
import type { BrickConfig } from "../objects/Brick.js";

/** 벽돌 1개의 배치 계획(spawn 입력) — 좌표 + BrickConfig. */
export interface BrickSpawnPlan {
  x: number;
  y: number;
  config: BrickConfig;
}

/** 챕터 그리드 계획 결과 — 벽돌 목록 + 멜로디 곡 음(멜로디 챕터만). */
export interface ChapterGridPlan {
  bricks: BrickSpawnPlan[];
  /** 멜로디 챕터이면 곡 음 ID 배열, 일반 챕터면 빈 배열. */
  melodyNoteIds: readonly string[];
}

/** 치트 플래그 — 미스터리/특수 벽돌 강제 스폰. */
export interface GridCheatFlags {
  forceMysteryBricks: boolean;
  forceSpecialBricks: boolean;
}

const SPACE_COMBAT_COLS = 4;
const SPACE_COMBAT_Y_OFFSET = 0;
const SPACE_FIELD_HP_BUDGET_SCALE = 0.7;
const SPACE_BLOCK_WEIGHTS = { single: 1 };
const SPACE_FLEET_HP_WEIGHTS = [0.72, 1, 1.38, 1.9] as const;
const MAX_INITIAL_VISIBLE_SPAWN_ROWS = 3;
type MarkedBrickCell = ReturnType<typeof markSpecialBricks>[number];
type SpaceFleetTierCell = Pick<MarkedBrickCell, "col" | "row" | "kind"> & {
  readonly hpMultiplier?: number;
};

/**
 * 챕터 벽돌 그리드 셀 계획(순수) — seed 결정론 + 미스터리/특수 마킹 + 레이아웃 배치.
 * 멜로디 챕터(매 MELODY.every)이면 곡 음 배치, 아니면 절차 생성.
 */
export function planChapterGrid(
  chapter: number,
  screenWidth: number,
  insets: SafeAreaInsets,
  contentOffset: number,
  cheats: GridCheatFlags,
  enemyScale = 1,
): ChapterGridPlan {
  const visualScale = normalizeEnemyScale(enemyScale);
  const starter = resolveStarterChapterTuning(chapter);
  const baseHp = ChapterController.computeBrickHp(chapter).mul(BigNumber.fromNumber(starter.brickHpMultiplier));
  const baseGold = ChapterController.computeRewardGold(chapter);
  const layout = resolveChapterLayout(chapter, MELODY.every);
  const world = worldForChapter(chapter);
  const isSpaceWorld = world.brickTextures.visualFamily === "space";
  const combatCols = isSpaceWorld ? SPACE_COMBAT_COLS : GRID.cols;
  const combatYOffset = isSpaceWorld ? SPACE_COMBAT_Y_OFFSET : 0;
  const blockWeights = isSpaceWorld ? SPACE_BLOCK_WEIGHTS : GRID.blockWeights;
  const fieldHpBudget = isSpaceWorld
    ? resolveSpaceFieldHpBudget(starter.seedHpBudget)
    : starter.seedHpBudget;

  if (layout.kind === "melody") {
    return planMelodyGrid(
      layout.noteIds,
      baseHp,
      baseGold,
      chapter,
      screenWidth,
      insets,
      contentOffset,
      fieldHpBudget,
      world.brickTextures,
      combatCols,
      combatYOffset,
      isSpaceWorld,
      visualScale,
    );
  }

  // 일반 챕터 — 절차 seed + 보상 상자(mystery) + 특수 벽돌 마킹 체인
  const { cells: baseCells, cols } = generateSeededBrickCells({
    rng: makeRng(chapter),
    cols: combatCols,
    minRows: starter.seedMinRows,
    maxRows: starter.seedMaxRows,
    density: starter.seedDensity,
    hpMultiplierBudget: fieldHpBudget,
    blockWeights,
    minBlocks: Math.min(MIN_CHAPTER_BLOCKS, fieldHpBudget),
  });

  const mysteryMarked = markMysteryBricks(baseCells, makeRng(chapter ^ 0x85ebca6b), {
    chance: cheats.forceMysteryBricks ? 1 : MYSTERY_BRICK.chance,
    maxPerChapter: cheats.forceMysteryBricks ? Number.MAX_SAFE_INTEGER : MYSTERY_BRICK.maxPerChapter,
  });

  const w = isSpaceWorld
    ? { shieldChance: 0, armorChance: 0, regenChance: 0 }
    : resolveSpecialBrickWeights(world.worldNo, SPECIAL_RULES);
  const cells = markSpecialBricks(mysteryMarked, makeRng(chapter ^ 0x27d4eb2f), {
    shieldChance: cheats.forceSpecialBricks ? 0.34 : w.shieldChance,
    armorChance: cheats.forceSpecialBricks ? 0.33 : w.armorChance,
    regenChance: cheats.forceSpecialBricks ? 0.33 : w.regenChance,
    shieldMax: cheats.forceSpecialBricks ? Number.MAX_SAFE_INTEGER : SHIELD_BRICK.maxPerChapter,
    armorMax: cheats.forceSpecialBricks ? Number.MAX_SAFE_INTEGER : ARMOR_BRICK.maxPerChapter,
    regenMax: cheats.forceSpecialBricks ? Number.MAX_SAFE_INTEGER : REGEN_BRICK.maxPerChapter,
  });

  const bricks = layoutToBrickPlans(
    cells,
    cols,
    baseHp,
    baseGold,
    chapter,
    screenWidth,
    insets,
    contentOffset,
    world.brickTextures,
    combatYOffset,
    isSpaceWorld,
    visualScale,
  );
  return { bricks, melodyNoteIds: [] };
}

function normalizeEnemyScale(scale: number): number {
  return Number.isFinite(scale) && scale > 0 ? scale : 1;
}

function resolveSpaceFieldHpBudget(seedHpBudget: number): number {
  return Math.max(MIN_CHAPTER_BLOCKS, Math.round(seedHpBudget * SPACE_FIELD_HP_BUDGET_SCALE));
}

function resolveStarterChapterTuning(chapter: number): {
  readonly brickHpMultiplier: number;
  readonly seedMinRows: number;
  readonly seedMaxRows: number;
  readonly seedDensity: number;
  readonly seedHpBudget: number;
} {
  if (chapter >= 1 && chapter <= GRID.starterChapter.rampEndChapter) {
    const rampSpan = Math.max(1, GRID.starterChapter.rampEndChapter - GRID.starterChapter.maxChapter);
    const t = chapter <= GRID.starterChapter.maxChapter
      ? 0
      : Math.min(1, Math.max(0, (chapter - GRID.starterChapter.maxChapter) / rampSpan));
    return {
      brickHpMultiplier: lerp(GRID.starterChapter.brickHpMultiplier, 1, t),
      seedMinRows: Math.round(lerp(GRID.starterChapter.seedMinRows, GRID.seedMinRows, t)),
      seedMaxRows: Math.round(lerp(GRID.starterChapter.seedMaxRows, GRID.seedMaxRows, t)),
      seedDensity: lerp(GRID.starterChapter.seedDensity, GRID.seedDensity, t),
      seedHpBudget: Math.round(lerp(GRID.starterChapter.seedHpBudget, GRID.seedHpBudget, t)),
    };
  }
  return {
    brickHpMultiplier: 1,
    seedMinRows: GRID.seedMinRows,
    seedMaxRows: GRID.seedMaxRows,
    seedDensity: GRID.seedDensity,
    seedHpBudget: GRID.seedHpBudget,
  };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** 멜로디 챕터 셀 계획(순수). */
function planMelodyGrid(
  noteIds: readonly string[],
  baseHp: BigNumber,
  baseGold: BigNumber,
  chapter: number,
  screenWidth: number,
  insets: SafeAreaInsets,
  contentOffset: number,
  hpBudget = MELODY.hpBudget,
  worldTextures?: WorldBrickTextures,
  cols: number = GRID.cols,
  yOffset = 0,
  hideHpLabels = false,
  enemyScale = 1,
): ChapterGridPlan {
  const bricks: BrickSpawnPlan[] = [];
  const placedCells = layoutBrickCells(
    buildMelodyCells(noteIds, cols, hpBudget),
    cols,
    screenWidth,
    insets,
    contentOffset,
  );
  const spaceFleetTiers = hideHpLabels
    ? resolveSpaceFleetTierPlan(placedCells, chapter)
    : undefined;
  for (const cell of placedCells) {
    const tier = spaceFleetTiers?.[bricks.length];
    const hpMultiplier = tier?.hpMultiplier ?? cell.hpMultiplier;
    const strength = tier?.strength ?? cell.strength ?? 0;
    const mult = BigNumber.fromNumber(hpMultiplier);
    bricks.push({
      x: cell.x,
      y: cell.y + yOffset,
      config: {
        hp: baseHp.mul(mult),
        rewardGold: baseGold.mul(mult),
        gridX: cell.col,
        gridY: cell.row,
        width: cell.width * enemyScale,
        height: cell.height * enemyScale,
        enemyScale,
        widthCells: cell.widthCells,
        heightCells: cell.heightCells,
        strength,
        kind: "normal",
        noteId: cell.noteId,
        shatterSalt: chapter,
        contentOffset,
        worldTextures,
        showHpLabel: !hideHpLabels,
      },
    });
  }
  return { bricks: moveOverflowRowsAboveGameview(bricks), melodyNoteIds: noteIds };
}

/** 던전 벽돌 그리드 셀 계획(순수) — index 비례 HP, 보상 골드 0. */
export function planDungeonGrid(
  index: number,
  screenWidth: number,
  insets: SafeAreaInsets,
  contentOffset: number,
  worldTextures?: WorldBrickTextures,
  enemyScale = 1,
): BrickSpawnPlan[] {
  const visualScale = normalizeEnemyScale(enemyScale);
  const hp = DungeonController.brickHp(index);
  const { cells, cols } = generateSeededBrickCells({
    rng: makeRng(index + 1_000_000),
    cols: GRID.cols,
    minRows: GRID.seedMinRows,
    maxRows: GRID.seedMaxRows,
    density: GRID.seedDensity,
    hpMultiplierBudget: GRID.seedHpBudget,
    blockWeights: GRID.blockWeights,
    minBlocks: MIN_CHAPTER_BLOCKS,
  });
  const bricks: BrickSpawnPlan[] = [];
  for (const cell of layoutBrickCells(cells, cols, screenWidth, insets, contentOffset)) {
    const mult = BigNumber.fromNumber(cell.hpMultiplier);
    bricks.push({
      x: cell.x,
      y: cell.y,
      config: {
        hp: hp.mul(mult),
        rewardGold: BigNumber.ZERO,
        gridX: cell.col,
        gridY: cell.row,
        width: cell.width * visualScale,
        height: cell.height * visualScale,
        enemyScale: visualScale,
        widthCells: cell.widthCells,
        heightCells: cell.heightCells,
        strength: brickStrength01(cell.hpMultiplier, CELL_MULTIPLIER_CAP),
        kind: cell.kind,
        shatterSalt: index + 1_000_000,
        contentOffset,
        worldTextures,
      },
    });
  }
  return bricks;
}

// ── 내부 헬퍼 ─────────────────────────────────────────────────────────────

/** 일반 챕터 레이아웃 셀 → BrickSpawnPlan 변환 (마킹 결과 포함). */
function layoutToBrickPlans(
  cells: ReturnType<typeof markSpecialBricks>,
  cols: number,
  baseHp: BigNumber,
  baseGold: BigNumber,
  chapter: number,
  screenWidth: number,
  insets: SafeAreaInsets,
  contentOffset: number,
  worldTextures?: WorldBrickTextures,
  yOffset = 0,
  hideHpLabels = false,
  enemyScale = 1,
): BrickSpawnPlan[] {
  const plans: BrickSpawnPlan[] = [];
  const spaceFleetTiers = hideHpLabels
    ? resolveSpaceFleetTierPlan(cells, chapter)
    : undefined;
  for (const cell of layoutBrickCells(cells, cols, screenWidth, insets, contentOffset)) {
    const tier = spaceFleetTiers?.[plans.length];
    const hpMultiplier = tier?.hpMultiplier ?? cell.hpMultiplier;
    const strength = tier?.strength ?? brickStrength01(cell.hpMultiplier, CELL_MULTIPLIER_CAP);
    const mult = BigNumber.fromNumber(hpMultiplier);
    const goldMul = specialBrickGoldMult(cell.kind);
    const hp = baseHp.mul(mult).mul(BigNumber.fromNumber(specialBrickHpFactor(cell.kind)));
    plans.push({
      x: cell.x,
      y: cell.y + yOffset,
      config: {
        hp,
        rewardGold: baseGold.mul(mult).mul(BigNumber.fromNumber(goldMul)),
        gridX: cell.col,
        gridY: cell.row,
        width: cell.width * enemyScale,
        height: cell.height * enemyScale,
        enemyScale,
        widthCells: cell.widthCells,
        heightCells: cell.heightCells,
        strength,
        kind: cell.kind,
        shatterSalt: chapter,
        contentOffset,
        worldTextures,
        showHpLabel: !hideHpLabels,
        ...specialBrickSpawnFields(cell.kind, baseHp, chapter),
      },
    });
  }
  return moveOverflowRowsAboveGameview(plans);
}

function moveOverflowRowsAboveGameview(plans: BrickSpawnPlan[]): BrickSpawnPlan[] {
  const overflowRows = resolveInitialSpawnOverflowRows(plans);
  if (overflowRows <= 0) return plans;
  const rowStep = GRID.brickHeight + GRID.vGap;
  const yOffset = overflowRows * rowStep + GRID.vGap;
  return plans.map((plan) => ({
    ...plan,
    y: plan.y - yOffset,
  }));
}

function resolveInitialSpawnOverflowRows(plans: readonly BrickSpawnPlan[]): number {
  if (plans.length === 0) return 0;
  let minRow = Number.POSITIVE_INFINITY;
  let maxRow = Number.NEGATIVE_INFINITY;
  for (const plan of plans) {
    const topRow = plan.config.gridY;
    const bottomRow = plan.config.gridY + Math.max(1, plan.config.heightCells ?? 1) - 1;
    minRow = Math.min(minRow, topRow);
    maxRow = Math.max(maxRow, bottomRow);
  }
  const rowCount = maxRow - minRow + 1;
  return Math.max(0, rowCount - MAX_INITIAL_VISIBLE_SPAWN_ROWS);
}

function resolveSpaceFleetTierPlan(
  cells: readonly SpaceFleetTierCell[],
  chapter: number,
): Array<{ readonly hpMultiplier: number; readonly strength: number }> {
  const tiers = cells.map((cell) => resolveSpaceFleetTier(cell, chapter));
  const baseHpMultipliers = cells.map((cell) => cell.hpMultiplier ?? 1);
  const rawHpMultipliers = tiers.map((tier, index) => baseHpMultipliers[index]! * SPACE_FLEET_HP_WEIGHTS[tier]!);
  const baseSum = baseHpMultipliers.reduce((sum, value) => sum + value, 0);
  const rawSum = rawHpMultipliers.reduce((sum, value) => sum + value, 0);
  const normalizer = rawSum > 0 && baseSum > 0 ? rawSum / baseSum : 1;
  const maxTier = SPACE_FLEET_HP_WEIGHTS.length - 1;
  return tiers.map((tier, index) => ({
    hpMultiplier: rawHpMultipliers[index]! / normalizer,
    strength: tier / maxTier,
  }));
}

function resolveSpaceFleetTier(
  cell: SpaceFleetTierCell,
  chapter: number,
): 0 | 1 | 2 | 3 {
  if (cell.kind !== "normal") return 0;
  const maxTier = chapter >= 10 ? 3 : chapter >= 3 ? 2 : 1;
  const noise = spaceFleetNoise01(cell.col, cell.row, chapter);
  const laneScore = ((cell.row % 4) * 0.11) + ((cell.col % 4) * 0.07) + noise * 0.65;
  const tier = laneScore > 0.88 ? 3 : laneScore > 0.72 ? 2 : laneScore > 0.45 ? 1 : 0;
  return Math.min(tier, maxTier) as 0 | 1 | 2 | 3;
}

function spaceFleetNoise01(col: number, row: number, chapter: number): number {
  const mixed =
    Math.imul(col + 11, 73_428_767) ^
    Math.imul(row + 17, 912_931) ^
    Math.imul(chapter + 23, 1_274_126_177);
  return (mixed >>> 0) / 4_294_967_295;
}
